import { StrategyType } from './strategy-type.model';
import { InvestmentZone } from './investment-zone.model';
import { Currency } from './currency.model';
import { MarketPositions } from './market-positions.model';

export class StrategyParameters {
  public strategy_type: StrategyType;
  public investment_zone: InvestmentZone;
  public currency: Currency;
  public market_positions: MarketPositions;

  public is_customized: boolean = false;
  public customize_exposure: boolean = false;
  public customize_risk_contributions: boolean = false;
  public customize_volatility: boolean = false;

  public is_long_short: boolean;
  public min_volatility: number;
  public max_volatility: number;
  public target_volatility: number;

  public size: number;
  public value: number;
  public momentum: number;
  public volatility: number;
  public high_yield: number;
  public quality: number;

  public size_risk_contribution: number;
  public value_risk_contribution: number;
  public momentum_risk_contribution: number;
  public volatility_risk_contribution: number;
  public high_yield_risk_contribution: number;
  public quality_risk_contribution: number;

  public size_risk_contribution_pct: number;
  public value_risk_contribution_pct: number;
  public momentum_risk_contribution_pct: number;
  public volatility_risk_contribution_pct: number;
  public high_yield_risk_contribution_pct: number;
  public quality_risk_contribution_pct: number;

  public factor_intensity: number = undefined;
  public size_contribution: number = undefined;
  public value_contribution: number = undefined;
  public momentum_contribution: number = undefined;
  public volatility_contribution: number = undefined;
  public high_yield_contribution: number = undefined;
  public quality_contribution: number = undefined;

  // initialize current portfolio from JS object
  initFromData(data: any) {
    if (data==undefined) {return}

    this.strategy_type     = data.strategy_type;
    this.investment_zone   = data.investment_zone;
    this.currency          = data.currency;
    this.market_positions  = data.market_positions;

    this.is_customized                = data.is_customized;
    this.customize_exposure           = data.customize_exposure;
    this.customize_risk_contributions = data.customize_risk_contributions;
    this.customize_volatility         = data.customize_volatility;

    this.is_long_short     = data.is_long_short;
    this.min_volatility    = 0.1;
    this.max_volatility    = 0.3;
    this.target_volatility = data.target_volatility;

    this.size          = data.size;
    this.value         = data.value;
    this.momentum      = data.momentum;
    this.volatility    = data.volatility;
    this.high_yield    = data.high_yield;
    this.quality       = data.quality;

    this.initFactorRiskContribution(data);

    this.computeFactorIntensityContribution();
    this.computeFactorRiskContributionPct();
  }

  initFactorRiskContribution(data: any) {
    const keys_risk_contributions: string[] = [
      'size_risk_contribution',
      'value_risk_contribution',
      'momentum_risk_contribution',
      'volatility_risk_contribution',
      'high_yield_risk_contribution',
      'quality_risk_contribution',
    ];

    const total_risk_contributions = keys_risk_contributions.map(
      (key) => (Math.max(data[key], 0))
    ).reduce((tot, risk_contrib) => (tot + risk_contrib), 0);

    keys_risk_contributions.forEach(
      (key) => {this[key] = Math.max(data[key], 0)/total_risk_contributions;}
    );
  }

  computeFactorIntensityContribution() {
    if (this.size==undefined || this.value==undefined || this.momentum==undefined || this.volatility==undefined || this.high_yield==undefined || this.quality==undefined) {
      return;
    }

    this.factor_intensity = this.size + this.value + this.momentum + this.volatility + this.high_yield + this.quality;

    this.size_contribution = this.size / this.factor_intensity;
    this.value_contribution = this.value / this.factor_intensity;
    this.momentum_contribution = this.momentum / this.factor_intensity;
    this.volatility_contribution = this.volatility / this.factor_intensity;
    this.high_yield_contribution = this.high_yield / this.factor_intensity;
    this.quality_contribution = this.quality / this.factor_intensity;
  }

  computeFactorRiskContributionPct() {
    if (this.size==undefined || this.value==undefined || this.momentum==undefined || this.volatility==undefined || this.high_yield==undefined || this.quality==undefined) {
      return;
    }
    const keys_risk_contributions: string[] = [
      'size_risk_contribution',
      'value_risk_contribution',
      'momentum_risk_contribution',
      'volatility_risk_contribution',
      'high_yield_risk_contribution',
      'quality_risk_contribution',
    ];

    const total_risk_contributions = keys_risk_contributions.map(
      (key) => (this[key])
    ).reduce((tot, risk_contrib) => (tot + risk_contrib), 0);

    this.size_risk_contribution_pct = this.size_risk_contribution / total_risk_contributions;
    this.value_risk_contribution_pct = this.value_risk_contribution / total_risk_contributions;
    this.momentum_risk_contribution_pct = this.momentum_risk_contribution / total_risk_contributions;
    this.volatility_risk_contribution_pct = this.volatility_risk_contribution / total_risk_contributions;
    this.high_yield_risk_contribution_pct = this.high_yield_risk_contribution / total_risk_contributions;
    this.quality_risk_contribution_pct = this.quality_risk_contribution / total_risk_contributions;
  }

  customization(custom_step: string, do_customize: boolean) {
    if (custom_step=='exposure') {
      this.customize_exposure = do_customize;
    } else if (custom_step=='risk_contributions') {
      this.customize_risk_contributions = do_customize;
    } else if (custom_step=='volatility') {
      this.customize_volatility = do_customize;
    }
    this.is_customized = this.customize_exposure || this.customize_risk_contributions || this.customize_volatility;
  }

}
